import { DialogContentText } from "@material-ui/core";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton/ConfirmButton";
import { WindowTitle } from "@saleor/components/WindowTitle";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { PaymentType } from "@saleor/motherOrders/components/OrderListPage";
import { useExportFile, useOrderListQuery } from "@saleor/motherOrders/queries";
import { getUrlParam } from "@saleor/motherOrders/views/OrderList/handlers";
import RemittanceListPage from "@saleor/remittance/components/remittanceListPage";
import { useExportOrders } from "@saleor/remittance/mutations";
import { useNonRemittanceSummaryQuery } from "@saleor/remittance/queries";
import {
  RemittanceListQueryParams,
  remittanceListUrl,
  RemittanceListUrlDialog
} from "@saleor/remittance/url";
import {
  getMoment,
  hasAppliedFilters
} from "@saleor/reports/PayoutOrders/utils/handlers";
import { ListViews } from "@saleor/types";
import { getGteLteVariables } from "@saleor/utils/filters";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import Moment from "moment";
import React, { useEffect, useState } from "react";
import { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  CourierFilter,
  ExportScope,
  FileTypesEnum,
  JobStatusEnum,
  OrderDirection,
  OrderSortField,
  SmopOrderStatusEnum
} from "../../../types/globalTypes";
import { getFilterVariables } from "./filters";

interface RemittanceListProps {
  params: RemittanceListQueryParams;
}

const RemittanceList: FC<RemittanceListProps> = ({ params }) => {
  const intl = useIntl();
  const paginate = usePaginator();
  const navigate = useNavigator();
  const notify = useNotifier();

  const orderDate = params?.orderDate;
  const dtToday = getMoment("YYYY-MM-DD");
  const dtMonthAgo = getMoment("YYYY-MM-DD", -31);

  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const [exportFileID, setExportFileID] = useState<string | undefined>();

  useEffect(() => {
    navigate(remittanceListUrl(params));
  }, []);

  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_LIST
  );
  const paginationState = createPaginationState(
    settings?.rowNumber ? settings?.rowNumber : 0,
    params
  );
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params, settings.rowNumber]
  );

  const {
    data: remittancePandagoData,
    refetch: refetchPandagoRemittance,
    loading: remittancePandagoOrdersLoading
  } = useOrderListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: {
        courierTypes: [CourierFilter.PANDAGO, CourierFilter.PANDAGO_AUTO],
        created: params?.orderDate
          ? { gte: params.orderDate, lte: params.orderDate }
          : null,
        noCollectionRequired: true,
        paymentMethod: PaymentType.CASH,
        smopStatus: [SmopOrderStatusEnum.COMPLETED]
      },
      sort: {
        direction: OrderDirection.ASC,
        field: OrderSortField.CREATION_DATE
      }
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => remittancePandagoData.orders.pageInfo),
    paginationState,
    params
  );

  const formatDateTime = (date: string, time: string) =>
    Moment(`${date} ${time}`).format("YYYY-MM-DDTHH:mm:ss[Z]");
  const orderDateRange = orderDate
    ? {
        gte: formatDateTime(orderDate, "00:00:00"),
        lte: formatDateTime(orderDate, "23:59:59")
      }
    : null;

  const {
    data: nonRemittanceSummaryData,
    loading: nonRemittanceSummaryDataLoading,
    refetch: refetchNonRemittanceSummaryData
  } = useNonRemittanceSummaryQuery({
    displayLoader: true,
    variables: {
      courier: CourierFilter.PANDAGO,
      orderDate: orderDateRange
    }
  });

  React.useEffect(() => {
    refetchPandagoRemittance();
    refetchNonRemittanceSummaryData();
  }, [settings?.rowNumber]);

  const [openModal, closeModal] = createDialogActionHandlers<
    RemittanceListUrlDialog,
    RemittanceListQueryParams
  >(navigate, remittanceListUrl, params);

  const handleConfirm = async () => {
    setExportFileID(null);
    setDownloadStatus("loading");

    const result = await exportOrders({
      variables: {
        input: {
          fileName: `Express Delivery Report${getMoment("_YYYYMMDDHHmmss")}`,
          fileType: FileTypesEnum.CSV,
          filter: {
            courierTypes: [CourierFilter.PANDAGO, CourierFilter.PANDAGO_AUTO],
            created: getGteLteVariables({
              gte:
                !getUrlParam("createdFrom") && getUrlParam("shippingFrom")
                  ? null
                  : hasAppliedFilters(params)
                  ? getMoment("YYYY-MM-DD", -90)
                  : params.orderDate || dtMonthAgo,
              lte:
                !getUrlParam("createdTo") && getUrlParam("shippingTo")
                  ? null
                  : hasAppliedFilters(params)
                  ? dtToday
                  : params.orderDate || dtToday
            }),
            noCollectionRequired: true,
            paymentMethod: PaymentType.CASH
          },
          scope: ExportScope.FILTER
        }
      }
    });
    refetchExportFile();
    return result.data.exportOrders.errors;
  };

  const { data: ExportFileData, refetch: refetchExportFile } = useExportFile({
    displayLoader: true,
    skip:
      exportFileID === null || exportFileID === undefined || exportFileID === ""
        ? true
        : false,
    variables: { id: exportFileID }
  });

  const [exportOrders] = useExportOrders({
    onCompleted: ({ exportOrders }) => {
      if (exportOrders?.errors?.length === 0) {
        setExportFileID(exportOrders?.exportFile?.id);
      }
    },
    onError: errors => {
      const [error] = errors.graphQLErrors;

      setDownloadStatus("default");
      setExportFileID(null);
      notify({
        status: "error",
        text: error?.message
      });
    }
  });

  React.useEffect(() => {
    if (ExportFileData?.exportFile !== undefined) {
      if (ExportFileData?.exportFile?.status === JobStatusEnum.SUCCESS) {
        window.open(
          ExportFileData?.exportFile?.url,
          "_blank",
          "noopener,noreferrer"
        );
        setDownloadStatus("success");
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Fulfillment successfully downloaded."
          })
        });
        closeModal();
      } else if (ExportFileData?.exportFile?.status === JobStatusEnum.FAILED) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Download Failed"
          })
        });
        setDownloadStatus("error");
        closeModal();
      } else {
        setDownloadStatus("loading");
        refetchExportFile();
      }
    }
  }, [ExportFileData]);

  const cancelDownload = () => {
    setDownloadStatus("default");
    closeModal();
  };

  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.remittance)} />
      <RemittanceListPage
        remittance={maybe(() =>
          remittancePandagoData.orders.edges.map(edge => edge.node)
        )}
        refetchPandagoRemittance={refetchPandagoRemittance}
        brands={undefined}
        onAdd={undefined}
        disabled={
          remittancePandagoOrdersLoading || nonRemittanceSummaryDataLoading
        }
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        pageInfo={pageInfo}
        onRowClick={undefined}
        params={params}
        onUpdateListSettings={updateListSettings}
        nonRemittanceSummary={nonRemittanceSummaryData?.nonRemittanceSummary}
        download={() => openModal("download")}
        settings={settings}
      />

      <ActionDialog
        open={params.action === "download"}
        onClose={cancelDownload}
        confirmButtonState={downloadStatus}
        onConfirm={() => {
          setDownloadStatus("loading");
          handleConfirm();
        }}
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Download Remittance Top Sheet",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage={
              "You are about to download a Remittance Top Sheet Reports. " +
              "Do you want to continue?"
            }
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};

export default RemittanceList;
